<template>
  <article
    class="flyer-card overflow-hidden border border-slate-200 bg-white shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
    :class="[
      view === 'post' ? 'rounded-xl' : 'mb-3 break-inside-avoid rounded-xl',
      {
        'flyer-card-review': isBusinessPost,
        'group hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md': isCardInteractive,
        'cursor-pointer': canOpenBusinessPost
      }
    ]"
    :role="canOpenBusinessPost ? 'link' : undefined"
    :tabindex="canOpenBusinessPost ? 0 : undefined"
    :aria-label="canOpenBusinessPost ? `Open ${isCheckin ? 'checkin' : 'review'} for ${businessName}` : undefined"
    @click="openReviewFlyer"
    @keydown="handleCardKeydown"
    @mouseenter="prepareBusinessAddressScroll"
    @mouseleave="resetBusinessAddressScroll"
  >
    <div class="relative">
      <div
        v-if="!reviewContentOnly && !isCheckin"
        class="absolute right-2 z-10"
        :class="isBusinessPost ? 'top-3' : 'top-2'"
      >
        <button
          class="grid h-6 w-6 place-items-center rounded-full bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200 backdrop-blur transition hover:bg-white hover:text-slate-950"
          type="button"
          aria-label="Flyer actions"
          @click.stop="menuOpen = !menuOpen"
        >
          <i class="ri-more-2-fill text-base" aria-hidden="true"></i>
        </button>

        <div v-if="menuOpen" class="absolute right-0 mt-2 grid min-w-[8.5rem] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl shadow-slate-950/10">
          <button v-if="!isReview && !isOwnFlyer" class="flyer-menu-item" type="button" @click.stop="toggleLike">
            <i class="text-base" :class="liked ? 'ri-heart-fill text-rose-500' : 'ri-heart-line'" aria-hidden="true"></i>
            <span>{{ liked ? 'Liked' : 'Like' }}</span>
          </button>
          <button v-if="!isReview && !isOwnFlyer" class="flyer-menu-item" type="button" @click.stop="openSaveBoard">
            <i class="text-base" :class="saved ? 'ri-bookmark-fill text-blue-600' : 'ri-bookmark-line'" aria-hidden="true"></i>
            <span>{{ saved ? 'Saved' : 'Save' }}</span>
          </button>
          <button class="flyer-menu-item" type="button" @click.stop="openShare">
            <i class="ri-share-forward-line shrink-0 text-base" aria-hidden="true"></i>
            <span>Share</span>
          </button>
          <button class="flyer-menu-item text-blue-700 hover:bg-blue-50" type="button" @click.stop="openReport">
            <i class="ri-flag-line shrink-0 text-base" aria-hidden="true"></i>
            <span>Report</span>
          </button>
        </div>
      </div>

      <router-link v-if="!isBusinessPost" :to="`/flyers/${flyer.id}`" class="block">
        <div
          v-if="flyer.image"
          class="flyer-card-media bg-slate-100"
          :class="view === 'post' ? 'flyer-card-media-post' : 'flyer-card-media-masonry'"
        >
          <img
            class="h-auto w-full"
            :src="flyer.image"
            :alt="flyer.title || categoryLabel || 'Flyer image'"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
          />
        </div>
        <div
          v-else
          class="flyer-card-empty flex items-center justify-center text-white"
          :class="view === 'post' ? 'flyer-card-empty-post' : 'flyer-card-empty-masonry'"
          :style="{ background: fallbackBackground }"
        >
          <div class="min-w-0 text-center">
            <p class="m-0 line-clamp-5 text-sm font-normal leading-6 text-white sm:text-base">
              {{ textPostBody }}
            </p>
          </div>
        </div>
      </router-link>
    </div>

    <div class="grid gap-1.5 p-2.5 sm:gap-2 sm:p-3.5">
      <div v-if="isReview && !reviewContentOnly" class="review-card-content">
        <header class="review-author-header pr-9">
          <router-link class="flex min-w-0 flex-1 items-center gap-2.5" :to="authorRoute" @click.stop>
            <img
              class="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm outline outline-1 outline-slate-200"
              :src="authorAvatar"
              :alt="authorName"
              loading="lazy"
              decoding="async"
            />
            <span class="min-w-0 flex-1">
              <strong class="block truncate text-sm font-semibold capitalize text-slate-950">{{ authorName }}</strong>
              <span class="mt-0.5 flex min-w-0 items-center gap-1.5 text-[0.7rem] text-slate-500">
                <span>Reviewed a business</span>
                <span v-if="relativeDateLabel" aria-hidden="true">•</span>
                <span v-if="relativeDateLabel" class="truncate">{{ relativeDateLabel }}</span>
              </span>
            </span>
          </router-link>
        </header>

        <section class="review-business-panel">
          <span class="review-business-mark">{{ businessInitial }}</span>
          <span class="min-w-0 flex-1">
            <strong class="block text-sm font-semibold leading-5 text-slate-950">{{ businessName }}</strong>
            <span class="mt-1 flex items-start gap-1.5 text-xs leading-4 text-slate-500">
              <i class="ri-map-pin-2-line mt-0.5 shrink-0 text-blue-600" aria-hidden="true"></i>
              <span class="min-w-0 break-words">
                <span>{{ displayedBusinessAddress }}</span>
                <button
                  v-if="isBusinessAddressTruncated"
                  class="ml-1 font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  type="button"
                  :aria-expanded="businessAddressExpanded"
                  @click.stop="businessAddressExpanded = !businessAddressExpanded"
                >
                  {{ businessAddressExpanded ? 'Less' : 'More' }}
                </button>
              </span>
            </span>
          </span>
          <a
            v-if="businessMapsUrl"
            class="review-map-button"
            :href="businessMapsUrl"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`Open ${businessName} in Google Maps`"
            title="Open in Google Maps"
            @click.stop
            @keydown.stop
          >
            <i class="ri-map-2-line" aria-hidden="true"></i>
          </a>
        </section>

        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-1" :aria-label="`${reviewRating} out of 5 stars`">
            <i
              v-for="star in 5"
              :key="star"
              class="text-base"
              :class="star <= reviewRating ? `ri-star-fill ${reviewTone.starClass}` : 'ri-star-line text-slate-300'"
              aria-hidden="true"
            ></i>
            <strong class="ml-1 text-xs text-slate-600">{{ reviewRating }}/5</strong>
          </div>
          <span class="review-tone-badge" :class="reviewTone.badgeClass">
            <i :class="reviewTone.iconClass" aria-hidden="true"></i>
            <span>{{ reviewTone.shortLabel }}</span>
          </span>
        </div>

        <div class="review-copy">
          <i class="ri-double-quotes-l review-quote" aria-hidden="true"></i>
          <p class="m-0 text-sm leading-5 text-slate-700" :class="{ 'line-clamp-5': isReviewCommentTruncated && !commentExpanded }">
            {{ fullReviewComment }}
          </p>
          <button
            v-if="isReviewCommentTruncated"
            class="mt-1 w-fit text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
            type="button"
            :aria-expanded="commentExpanded"
            @click.stop="commentExpanded = !commentExpanded"
          >
            {{ commentExpanded ? 'Read less' : 'Read more' }}
          </button>
        </div>

        <Swiper
          v-if="reviewImages.length"
          class="review-image-strip"
          :modules="reviewSwiperModules"
          :slides-per-view="3"
          :slides-per-group="1"
          :space-between="8"
          :watch-overflow="true"
          :pagination="{ clickable: true, dynamicBullets: true }"
          aria-label="Review photos"
          @click.stop
        >
          <SwiperSlide v-for="(image, imageIndex) in reviewImages" :key="`${image}-${imageIndex}`">
            <div class="review-image-tile">
              <img :src="image" :alt="`${businessName} review photo ${imageIndex + 1}`" loading="lazy" decoding="async" />
            </div>
          </SwiperSlide>
        </Swiper>

        <footer class="review-engagement-row">
          <button
            class="review-engagement-action"
            :class="{ 'is-liked': liked }"
            type="button"
            :disabled="likeSaving || isOwnFlyer"
            :aria-label="isOwnFlyer ? 'Like unavailable for your review' : likeCountLabel"
            @click.stop="toggleLike"
          >
            <i :class="liked ? 'ri-heart-fill' : 'ri-heart-line'" aria-hidden="true"></i>
            <span>{{ likesCount }}</span>
          </button>
          <router-link class="review-engagement-action" :to="businessPostDetailRoute" :aria-label="`${commentsCount} comments`" @click.stop>
            <i class="ri-chat-3-line" aria-hidden="true"></i>
            <span>{{ commentsCount }}</span>
          </router-link>
        </footer>
      </div>

      <div v-else-if="isBusinessPost" class="grid gap-2.5">
        <div
          v-if="!reviewContentOnly"
          class="flex min-w-0 items-center gap-2.5 rounded-xl bg-slate-50 p-2.5 transition group-hover:bg-slate-100"
          :class="{ 'mr-8': !isCheckin }"
        >
          <span class="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-600 text-sm font-bold text-white">
            {{ businessInitial }}
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-semibold text-slate-950">{{ businessName }}</span>
            <span class="mt-0.5 flex min-w-0 items-center gap-1 text-[0.7rem] text-slate-500">
              <i class="ri-map-pin-2-line shrink-0 text-blue-600" aria-hidden="true"></i>
              <span
                ref="businessAddressViewport"
                class="business-address-viewport"
                :title="businessAddress || 'Address not available'"
              >
                <span
                  ref="businessAddressText"
                  class="business-address-text"
                  :class="{ 'business-address-text-scrolling': businessAddressScrollable }"
                  :style="businessAddressScrollStyle"
                >
                  {{ businessAddress || 'Address not available' }}
                </span>
              </span>
            </span>
          </span>
          <a
            v-if="showBusinessMapLink && businessMapsUrl"
            class="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-blue-600 shadow-sm ring-1 ring-slate-200 transition hover:bg-blue-50 hover:text-blue-800 hover:ring-blue-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            :href="businessMapsUrl"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`Open ${businessName} in Google Maps`"
            :title="`Open ${businessName} in Google Maps`"
            @click.stop
            @keydown.stop
          >
            <i class="ri-map-2-line text-base" aria-hidden="true"></i>
          </a>
        </div>

        <div v-if="isReview" class="flex items-center gap-2.5">
          <span
            v-if="reviewContentOnly"
            class="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl"
            :class="reviewTone.badgeClass"
            :aria-label="reviewTone.label"
            role="img"
          >
            <i class="review-rating-emoji" :class="reviewTone.iconClass" aria-hidden="true"></i>
          </span>

          <div class="flex items-center gap-1" :aria-label="`${reviewRating} out of 5 stars`">
            <i
              v-for="star in 5"
              :key="star"
              class="text-base"
              :class="star <= reviewRating ? `ri-star-fill ${reviewTone.starClass}` : 'ri-star-line text-slate-300'"
              aria-hidden="true"
            ></i>
            <span class="ml-1 text-xs font-semibold text-slate-500">{{ reviewRating }}/5</span>
          </div>
        </div>

        <p
          class="m-0 text-sm font-normal leading-5 text-slate-700"
          :class="{ 'line-clamp-5': !collapsibleComment || !commentExpanded }"
        >
          {{ displayedReviewComment }}
        </p>
        <button
          v-if="isReviewCommentTruncated"
          class="w-fit text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
          type="button"
          :aria-expanded="commentExpanded"
          @click.stop="commentExpanded = !commentExpanded"
        >
          {{ commentExpanded ? 'Read less' : 'Read more' }}
        </button>

        <Swiper
          v-if="isReview && reviewImages.length"
          class="review-image-strip"
          :modules="reviewSwiperModules"
          :slides-per-view="3"
          :slides-per-group="1"
          :space-between="8"
          :watch-overflow="true"
          :pagination="{ clickable: true, dynamicBullets: true }"
          aria-label="Review photos"
        >
          <SwiperSlide
            v-for="(image, imageIndex) in reviewImages"
            :key="`${image}-${imageIndex}`"
          >
            <div
              class="review-image-tile"
            >
              <img
                :src="image"
                :alt="`${businessName} review photo ${imageIndex + 1}`"
                loading="lazy"
                decoding="async"
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      <div v-if="!reviewContentOnly && !isReview" class="flex min-w-0 items-center gap-1.5 text-[0.66rem] text-slate-500 sm:gap-3 sm:text-[0.78rem]">
        <div
          v-if="!isBusinessPost"
          class="flex min-w-0 flex-1 items-center gap-1 sm:gap-1.5"
        >
          <i class="ri-map-pin-2-line shrink-0 text-sm text-blue-600 sm:text-base" aria-hidden="true"></i>
          <span class="truncate">{{ locationLabel }}</span>
        </div>

        <span v-if="relativeDateLabel" class="flex shrink-0 items-center gap-1" :class="{ 'ml-auto': isBusinessPost }">
          <i class="ri-time-line text-xs text-slate-400 sm:text-base" aria-hidden="true"></i>
          <span>{{ relativeDateLabel }}</span>
        </span>
      </div>

      <div v-if="showAuthor && !isReview" class="flex min-w-0 items-center gap-1.5 border-t border-slate-100 pt-1.5 sm:gap-2 sm:pt-2">
        <router-link class="flex min-w-0 flex-1 items-center gap-1.5 hover:text-blue-700 sm:gap-2" :to="authorRoute" @click.stop>
          <img
            class="h-5 w-5 shrink-0 rounded-full object-cover ring-1 ring-slate-200 sm:h-7 sm:w-7"
            :src="authorAvatar"
            :alt="flyer.author || 'NetFlyr'"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
          />
          <span class="truncate text-[0.68rem] font-normal text-slate-500 sm:text-[0.82rem]">
            {{ flyer.author || 'NetFlyr' }}
          </span>
        </router-link>
      </div>
    </div>

    <Teleport to="body">
      <ShareFlyerModal v-if="shareModalOpen" :flyer="flyer" @close="shareModalOpen = false" />
      <ReportFlyerModal v-if="reportModalOpen" :flyer="flyer" @close="reportModalOpen = false" @submit="handleReport" />
      <SaveToBoardModal
        v-if="saveBoardModalOpen"
        :flyer-id="flyer.id"
        :initial-saved="saved"
        :initial-board-id="savedBoardId"
        @close="saveBoardModalOpen = false"
        @saved-change="handleSavedChange"
      />
    </Teleport>
  </article>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import ShareFlyerModal from '@/components/common/ShareFlyerModal.vue'
import ReportFlyerModal from '@/components/common/ReportFlyerModal.vue'
import SaveToBoardModal from '@/components/common/SaveToBoardModal.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { getFlyerBoardId, normalizeFlyerSaved } from '@/utils/flyerDetail'
import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps({
  flyer: { type: Object, required: true },
  view: { type: String, default: 'masonry' },
  showAuthor: { type: Boolean, default: true },
  reviewContentOnly: { type: Boolean, default: false },
  businessPostClickable: { type: Boolean, default: true },
  showBusinessMapLink: { type: Boolean, default: false },
  collapsibleComment: { type: Boolean, default: false }
})

const menuOpen = ref(false)
const shareModalOpen = ref(false)
const reportModalOpen = ref(false)
const saveBoardModalOpen = ref(false)
const commentExpanded = ref(false)
const businessAddressExpanded = ref(false)
const likeSaving = ref(false)
const businessAddressViewport = ref(null)
const businessAddressText = ref(null)
const businessAddressScrollable = ref(false)
const businessAddressScrollStyle = ref({})
const reviewSwiperModules = [Pagination]
const normalizeLiked = (flyer = {}) => {
  const status = [flyer.liked, flyer.is_liked, flyer.like_status, flyer.liked_status]
    .find((value) => value !== undefined && value !== null && value !== '')
  return ['1', 'true', 'yes', 'liked'].includes(String(status || '').trim().toLowerCase())
}
const liked = ref(normalizeLiked(props.flyer))
const localLikesCount = ref(Number(props.flyer.likesCount || props.flyer.likes_count || props.flyer.like_count || 0))
const saved = ref(normalizeFlyerSaved(props.flyer))
const savedBoardId = ref(String(getFlyerBoardId(props.flyer) || '').trim())
const router = useRouter()
const session = useSessionStore()

const currentUserId = computed(() => String(session.userId || session.user?.user_id || session.user?.id || '').trim())
const flyerOwnerId = computed(() =>
  String(
    props.flyer.userId ||
    props.flyer.user_id ||
    props.flyer.userid ||
    props.flyer.uid ||
    props.flyer.created_by ||
    props.flyer.created_by_id ||
    props.flyer.owner_id ||
    props.flyer.author_id ||
    ''
  ).trim()
)
const isOwnFlyer = computed(() => currentUserId.value && flyerOwnerId.value && currentUserId.value === flyerOwnerId.value)
const flyerType = computed(() => String(props.flyer.flyer_type || props.flyer.flyerType || '').toLowerCase())
const isCheckin = computed(() => flyerType.value === 'checkin')
const isReview = computed(() => (
  flyerType.value === 'review' ||
  (!flyerType.value && Boolean(props.flyer.business_name || props.flyer.businessName || props.flyer.s_business || props.flyer.review_comment || props.flyer.review))
))
const isBusinessPost = computed(() => isReview.value || isCheckin.value)
const canOpenBusinessPost = computed(() => props.businessPostClickable && ((isReview.value && Boolean(props.flyer.id)) || isCheckin.value))
const isCardInteractive = computed(() => !isBusinessPost.value || canOpenBusinessPost.value)
const businessName = computed(() => String(props.flyer.business_name || props.flyer.businessName || props.flyer.s_business || props.flyer.title || 'Business').trim())
const businessAddress = computed(() => String(props.flyer.business_adress || props.flyer.business_address || props.flyer.businessAddress || props.flyer.s_address || props.flyer.location || props.flyer.address || '').trim())
const businessCoordinates = computed(() => {
  const rawLatitude = props.flyer.business_lat ?? props.flyer.businessLatitude ?? props.flyer.business_latitude ?? props.flyer.s_latit
  const rawLongitude = props.flyer.business_long ?? props.flyer.businessLong ?? props.flyer.business_longitude ?? props.flyer.s_longit
  if (String(rawLatitude ?? '').trim() === '' || String(rawLongitude ?? '').trim() === '') return null
  const latitude = Number(rawLatitude)
  const longitude = Number(rawLongitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  return { latitude, longitude }
})
const businessMapsUrl = computed(() => {
  if (businessCoordinates.value) {
    const { latitude, longitude } = businessCoordinates.value
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`
  }
  if (businessAddress.value) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddress.value)}`
  }
  return ''
})
const businessInitial = computed(() => businessName.value.charAt(0).toUpperCase() || 'B')
const authorName = computed(() => String(props.flyer.author || props.flyer.user_name || props.flyer.username || 'NetFlyr').trim())
const businessAddressPreviewLimit = 40
const isBusinessAddressTruncated = computed(() => businessAddress.value.length > businessAddressPreviewLimit)
const displayedBusinessAddress = computed(() => {
  if (!businessAddress.value) return 'Address not available'
  if (!isBusinessAddressTruncated.value || businessAddressExpanded.value) return businessAddress.value
  return `${businessAddress.value.slice(0, businessAddressPreviewLimit).trimEnd()}…`
})

const prepareBusinessAddressScroll = async () => {
  if (!isCheckin.value) return
  await nextTick()
  const viewport = businessAddressViewport.value
  const text = businessAddressText.value
  if (!viewport || !text) return

  const distance = Math.max(0, text.scrollWidth - viewport.clientWidth)
  businessAddressScrollable.value = distance > 2
  businessAddressScrollStyle.value = distance > 2
    ? {
        '--business-address-scroll-distance': `${distance}px`,
        '--business-address-scroll-duration': `${Math.max(2.5, distance / 24).toFixed(2)}s`
      }
    : {}
}

const resetBusinessAddressScroll = () => {
  businessAddressScrollable.value = false
}
const reviewRating = computed(() => Math.min(5, Math.max(0, Number(props.flyer.review_count || props.flyer.rating || 0))))
const reviewTone = computed(() => {
  const score = Math.round(reviewRating.value)
  const tones = {
    1: { iconClass: 'ri-emotion-unhappy-line', badgeClass: 'bg-red-100 text-red-600', starClass: 'text-red-500', label: 'Very negative review', shortLabel: 'Poor' },
    2: { iconClass: 'ri-emotion-sad-line', badgeClass: 'bg-orange-100 text-orange-600', starClass: 'text-orange-500', label: 'Negative review', shortLabel: 'Fair' },
    3: { iconClass: 'ri-emotion-normal-line', badgeClass: 'bg-amber-100 text-amber-600', starClass: 'text-amber-400', label: 'Neutral review', shortLabel: 'Okay' },
    4: { iconClass: 'ri-emotion-happy-line', badgeClass: 'bg-lime-100 text-lime-700', starClass: 'text-lime-500', label: 'Positive review', shortLabel: 'Good' },
    5: { iconClass: 'ri-emotion-laugh-line', badgeClass: 'bg-emerald-100 text-emerald-600', starClass: 'text-emerald-500', label: 'Excellent review', shortLabel: 'Excellent' }
  }

  return tones[score] || {
    iconClass: 'ri-chat-smile-2-line',
    badgeClass: 'bg-slate-100 text-slate-500',
    starClass: 'text-slate-400',
    label: 'Unrated review',
    shortLabel: 'Unrated'
  }
})
const reviewImages = computed(() => Array.from(new Set([
  ...(Array.isArray(props.flyer.images) ? props.flyer.images : []),
  props.flyer.image,
  props.flyer.image1,
  props.flyer.image2,
  props.flyer.image3,
  props.flyer.image4
].filter(Boolean))))
const authorRoute = computed(() => {
  if (isOwnFlyer.value) return '/account'
  return `/users/${props.flyer.userId || props.flyer.user_id || props.flyer.userid || props.flyer.uid || props.flyer.id}`
})
const authorAvatar = computed(() => props.flyer.authorAvatar || `https://dummyimage.com/80x80/e0e7ff/1d4ed8&text=${encodeURIComponent((props.flyer.author || 'N').charAt(0))}`)
const categoryLabel = computed(() => {
  const category = String(props.flyer.category || '').trim()
  const namedCategory =
    props.flyer.category_name ||
    props.flyer.catergory_name ||
    props.flyer.cat_name ||
    props.flyer.content_type_name ||
    props.flyer.flyer_category ||
    props.flyer.categoryTitle ||
    props.flyer.category_title ||
    ''

  if (String(namedCategory || '').trim()) return namedCategory
  return category && Number.isNaN(Number(category)) ? category : 'Flyer'
})
const locationLabel = computed(() => {
  const parts = [
    props.flyer.city,
    props.flyer.location,
    props.flyer.area,
    props.flyer.locality,
    props.flyer.state,
    props.flyer.address,
    props.flyer.street,
    props.flyer.country_id,
    props.flyer.country
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  return parts[0] || 'Location not available'
})
const decodeHtmlEntities = (value = '') => {
  let decoded = String(value || '')
  if (!decoded) return ''

  for (let index = 0; index < 5; index += 1) {
    const previous = decoded
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = decoded
      decoded = textarea.value
    } else {
      decoded = decoded
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
    }
    if (decoded === previous) break
  }

  return decoded
}

const stripHtml = (value = '') =>
  decodeHtmlEntities(value)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const reviewComment = computed(() => stripHtml(props.flyer.review_comment || props.flyer.review || props.flyer.description || ''))
const reviewCommentFallback = computed(() => isCheckin.value ? 'No checkin comment provided.' : 'No review comment provided.')
const fullReviewComment = computed(() => reviewComment.value || reviewCommentFallback.value)
const commentCharacterLimit = 180
const isReviewCommentTruncated = computed(() => (
  (props.collapsibleComment || isReview.value) && fullReviewComment.value.length > commentCharacterLimit
))
const displayedReviewComment = computed(() => {
  if (!isReviewCommentTruncated.value || commentExpanded.value) return fullReviewComment.value
  return `${fullReviewComment.value.slice(0, commentCharacterLimit).trimEnd()}…`
})
const textPostBody = computed(() => stripHtml(props.flyer.description) || props.flyer.title || categoryLabel.value || 'Flyer')
const relativeDateLabel = computed(() => {
  return props.flyer.time_ago ||
    props.flyer.time_Ago ||
    props.flyer.timeAgo ||
    props.flyer.created_at ||
    props.flyer.postedAt ||
    props.flyer.post_date ||
    props.flyer.updated_at ||
    props.flyer.date ||
    ''
})
const normalizeHex = (value) => {
  const raw = String(value || '').replace('#', '').trim()
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return raw.split('').map((char) => `${char}${char}`).join('')
  }
  return /^[0-9a-f]{6}$/i.test(raw) ? raw : '2563eb'
}
const flyerCodeColor = computed(() =>
  props.flyer.param_code ||
  props.flyer.paramCode ||
  props.flyer.code ||
  props.flyer.color_code ||
  props.flyer.colorCode ||
  props.flyer.categoryColor ||
  props.flyer.accent ||
  '#2563eb'
)
const fallbackBackground = computed(() => `#${normalizeHex(flyerCodeColor.value)}`)
const likesCount = computed(() => Math.max(0, Number(localLikesCount.value) || 0))
const likeCountLabel = computed(() => `${liked.value ? 'Unlike' : 'Like'} review, ${likesCount.value} ${likesCount.value === 1 ? 'like' : 'likes'}`)
const commentsCount = computed(() => Math.max(0, Number(
  props.flyer.commentsCount ?? props.flyer.comments_count ?? props.flyer.comment_count ?? 0
) || 0))

const businessPostDetailRoute = computed(() => {
  if (!isCheckin.value) return `/flyers/${props.flyer.id}`

  return {
    path: `/flyers/${props.flyer.id}`,
    query: { type: 'checkin' },
    state: {
      checkin: JSON.parse(JSON.stringify(props.flyer))
    }
  }
})

const openReviewFlyer = (event) => {
  if (!canOpenBusinessPost.value) return
  const interactiveTarget = event?.target?.closest?.('a, button, input, select, textarea, [role="button"]')
  if (interactiveTarget && interactiveTarget !== event.currentTarget) return
  router.push(businessPostDetailRoute.value)
}

const handleCardKeydown = (event) => {
  if (!canOpenBusinessPost.value || event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  router.push(businessPostDetailRoute.value)
}

const openShare = () => {
  menuOpen.value = false
  shareModalOpen.value = true
}

const openReport = () => {
  menuOpen.value = false
  reportModalOpen.value = true
}

const handleReport = (payload) => {
  console.info('Flyer report submitted', { flyerId: props.flyer.id, ...payload })
}

const requireLogin = () => {
  if (session.isLoggedIn && (session.userId || session.user?.user_id)) return true
  router.push('/login')
  return false
}

const openSaveBoard = () => {
  menuOpen.value = false
  if (isOwnFlyer.value) return
  if (!requireLogin()) return
  saveBoardModalOpen.value = true
}

const handleSavedChange = ({ saved: nextSaved, boardIds = [] } = {}) => {
  saved.value = Boolean(nextSaved)
  savedBoardId.value = saved.value
    ? boardIds.map((boardId) => String(boardId || '').trim()).find(Boolean) || savedBoardId.value
    : ''
}

watch(
  () => props.flyer,
  (nextFlyer) => {
    liked.value = normalizeLiked(nextFlyer)
    localLikesCount.value = Number(nextFlyer.likesCount || nextFlyer.likes_count || nextFlyer.like_count || 0)
    saved.value = normalizeFlyerSaved(nextFlyer)
    savedBoardId.value = String(getFlyerBoardId(nextFlyer) || '').trim()
    commentExpanded.value = false
    businessAddressExpanded.value = false
  },
  { deep: true }
)

const toggleLike = async () => {
  menuOpen.value = false
  if (!props.flyer.id || likeSaving.value || isOwnFlyer.value) return
  if (!requireLogin()) return

  const previousLiked = liked.value
  const previousCount = likesCount.value
  liked.value = !liked.value
  localLikesCount.value = Math.max(0, previousCount + (liked.value ? 1 : -1))
  likeSaving.value = true

  try {
    await api.likeFlyer({
      flyer_id: props.flyer.id,
      user_id: session.userId || session.user?.user_id || ''
    })
  } catch {
    liked.value = previousLiked
    localLikesCount.value = previousCount
  } finally {
    likeSaving.value = false
  }
}
</script>

<style scoped>
.flyer-card {
  transform: translateZ(0);
}

.flyer-card-review {
  border-color: rgb(226 232 240);
}

.review-card-content {
  display: grid;
  gap: 0.75rem;
}

.review-author-header {
  display: flex;
  min-width: 0;
  align-items: center;
}

.review-business-panel {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 0.65rem;
  border: 1px solid rgb(219 234 254);
  border-radius: 0.9rem;
  background: linear-gradient(135deg, rgb(239 246 255), rgb(248 250 252));
  padding: 0.7rem;
}

.review-business-mark {
  display: grid;
  width: 2.35rem;
  height: 2.35rem;
  flex: none;
  place-items: center;
  border-radius: 0.7rem;
  background: rgb(37 99 235);
  color: white;
  font-size: 0.85rem;
  font-weight: 700;
  box-shadow: 0 5px 12px rgb(37 99 235 / 0.2);
}

.review-map-button {
  display: grid;
  width: 2rem;
  height: 2rem;
  flex: none;
  place-items: center;
  border: 1px solid rgb(191 219 254);
  border-radius: 9999px;
  background: white;
  color: rgb(37 99 235);
  transition: background 0.15s ease, transform 0.15s ease;
}

.review-map-button:hover {
  background: rgb(219 234 254);
  transform: translateY(-1px);
}

.review-tone-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 9999px;
  padding: 0.25rem 0.55rem;
  font-size: 0.68rem;
  font-weight: 600;
}

.review-copy {
  position: relative;
  border-left: 3px solid rgb(191 219 254);
  border-radius: 0 0.75rem 0.75rem 0;
  background: rgb(248 250 252);
  padding: 0.7rem 0.75rem 0.7rem 1rem;
}

.review-quote {
  position: absolute;
  top: -0.35rem;
  left: 0.35rem;
  color: rgb(147 197 253);
  font-size: 1.2rem;
}

.review-engagement-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-top: 1px solid rgb(241 245 249);
  padding-top: 0.55rem;
}

.review-engagement-action {
  display: inline-flex;
  min-width: 2.15rem;
  min-height: 2rem;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 9999px;
  padding: 0 0.45rem;
  color: rgb(71 85 105);
  font-size: 0.72rem;
  font-weight: 600;
  transition: background 0.15s ease, color 0.15s ease;
}

.review-engagement-action:hover {
  background: rgb(241 245 249);
  color: rgb(30 64 175);
}

.review-engagement-action:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.review-engagement-action.is-liked {
  color: rgb(244 63 94);
}

.review-engagement-action i {
  font-size: 1.1rem;
}

.business-address-viewport {
  display: block;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
}

.business-address-text {
  display: block;
  width: max-content;
  min-width: 100%;
  transform: translateX(0);
  will-change: transform;
}

.business-address-text-scrolling {
  animation: business-address-scroll var(--business-address-scroll-duration, 3s) ease-in-out 0.35s infinite alternate;
}

@keyframes business-address-scroll {
  to {
    transform: translateX(calc(-1 * var(--business-address-scroll-distance, 0px)));
  }
}

@media (prefers-reduced-motion: reduce) {
  .business-address-text-scrolling {
    animation: none;
  }
}

.review-rating-emoji {
  display: inline-block;
  line-height: 1;
  transform-origin: 50% 80%;
  animation: review-emoji-float 2.2s ease-in-out infinite;
}

@keyframes review-emoji-float {
  0%,
  100% {
    transform: translateY(0) rotate(-3deg) scale(1);
  }

  50% {
    transform: translateY(-3px) rotate(3deg) scale(1.08);
  }
}

.review-image-strip {
  width: 100%;
  max-width: 100%;
  padding: 0.1rem 0 1.25rem;
}

.review-image-strip :deep(.swiper-slide) {
  height: auto;
}

.review-image-strip :deep(.swiper-pagination) {
  bottom: 0.05rem;
}

.review-image-strip :deep(.swiper-pagination-bullet) {
  width: 0.38rem;
  height: 0.38rem;
  background: rgb(148 163 184);
  opacity: 0.65;
}

.review-image-strip :deep(.swiper-pagination-bullet-active) {
  background: rgb(37 99 235);
  opacity: 1;
}

.review-image-tile {
  display: grid;
  width: 100%;
  height: 100%;
  min-height: 3.5rem;
  aspect-ratio: 4 / 3;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.75rem;
  background: rgb(248 250 252);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.review-image-tile:hover {
  border-color: rgb(147 197 253);
  box-shadow: 0 6px 16px rgb(15 23 42 / 0.1);
  transform: translateY(-1px);
}

.review-image-tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.flyer-card-media {
  overflow: hidden;
}

.flyer-card-empty {
  padding: 1rem;
}

.flyer-card-empty-post {
  min-height: 21rem;
}

.flyer-card-empty-masonry {
  min-height: 10rem;
}

.flyer-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 2.25rem;
  padding: 0 0.8rem;
  text-align: left;
  font-size: 0.82rem;
  font-weight: 500;
  color: rgb(51 65 85);
}

.flyer-menu-item:hover {
  background: rgb(248 250 252);
  color: rgb(15 23 42);
}

.line-clamp-2,
.line-clamp-5 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  -webkit-line-clamp: 2;
}

.line-clamp-5 {
  -webkit-line-clamp: 5;
}

@media (max-width: 640px) {
  .flyer-card-empty-post {
    min-height: 18rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .review-rating-emoji {
    animation: none;
  }
}
</style>
